import { FC } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import type {
  ServiceFilters as ServiceFiltersType,
  ServiceCategory,
} from '../../types/service.types';

interface ServiceFiltersProps {
  filters: ServiceFiltersType;
  onFilterChange: (filters: Partial<ServiceFiltersType>) => void;
  onClearFilters: () => void;
  resultCount: number;
}

const CATEGORIES: Array<{ value: ServiceCategory | 'all'; label: string }> = [
  { value: 'all', label: 'All Services' },
  { value: 'Relaxation', label: 'Relaxation' },
  { value: 'Therapeutic', label: 'Therapeutic' },
  { value: 'Sports', label: 'Sports' },
  { value: 'Specialized', label: 'Specialized' },
];

const PRICE_RANGES: Array<{ label: string; min?: number; max?: number }> = [
  { label: 'Under £60', max: 60 },
  { label: '£60-£80', min: 60, max: 80 },
  { label: '£80-£100', min: 80, max: 100 },
  { label: 'Over £100', min: 100 },
];

const DURATION_RANGES: Array<{ label: string; min?: number; max?: number }> = [
  { label: 'Under 45 min', max: 45 },
  { label: '45-60 min', min: 45, max: 60 },
  { label: '60-90 min', min: 60, max: 90 },
  { label: 'Over 90 min', min: 90 },
];

export const ServiceFilters: FC<ServiceFiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  resultCount,
}) => {
  const hasActiveFilters =
    (filters.category && filters.category !== 'all') ||
    filters.minPrice !== undefined ||
    filters.maxPrice !== undefined ||
    filters.minDuration !== undefined ||
    filters.maxDuration !== undefined ||
    (filters.search && filters.search.trim() !== '');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ search: e.target.value });
  };

  const handleClearSearch = () => {
    onFilterChange({ search: '' });
  };

  const handleCategoryChange = (category: ServiceCategory | 'all') => {
    onFilterChange({ category });
  };

  const handlePriceRangeChange = (min?: number, max?: number) => {
    const isSameRange = filters.minPrice === min && filters.maxPrice === max;
    if (isSameRange) {
      onFilterChange({ minPrice: undefined, maxPrice: undefined });
    } else {
      onFilterChange({ minPrice: min, maxPrice: max });
    }
  };

  const handleDurationRangeChange = (min?: number, max?: number) => {
    const isSameRange = filters.minDuration === min && filters.maxDuration === max;
    if (isSameRange) {
      onFilterChange({ minDuration: undefined, maxDuration: undefined });
    } else {
      onFilterChange({ minDuration: min, maxDuration: max });
    }
  };

  const isPriceRangeSelected = (min?: number, max?: number) => {
    return filters.minPrice === min && filters.maxPrice === max;
  };

  const isDurationRangeSelected = (min?: number, max?: number) => {
    return filters.minDuration === min && filters.maxDuration === max;
  };

  return (
    <div className="backdrop-blur-sm bg-white/90 dark:bg-slate-800/90 rounded-2xl border border-primary-100/50 dark:border-slate-700/50 p-6 shadow-sm">
      {/* Header */}
      <div className="mb-6 flex items-center gap-2">
        <SlidersHorizontal className="h-5 w-5 text-gray-600 dark:text-slate-400" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Filters</h2>
      </div>

      {/* Search Input */}
      <div className="mb-6">
        <label htmlFor="service-search" className="mb-2 block text-sm font-medium text-gray-700 dark:text-slate-300">
          Search
        </label>
        <div className="relative">
          <Input
            id="service-search"
            type="text"
            placeholder="Search services..."
            value={filters.search || ''}
            onChange={handleSearchChange}
            leftIcon={<Search className="h-4 w-4" />}
            className="pr-10"
          />
          {filters.search && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="mb-6">
        <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-slate-300">Category</h3>
        <div className="flex flex-col gap-2">
          {CATEGORIES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => handleCategoryChange(value)}
              className={cn(
                'w-full rounded-full px-4 py-2 text-sm font-medium transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                filters.category === value || (value === 'all' && !filters.category)
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-white/50 dark:bg-slate-700/30 text-gray-700 dark:text-slate-300 hover:bg-white/80 dark:hover:bg-slate-600/50 border border-gray-200/50 dark:border-slate-600/50'
              )}
              aria-pressed={filters.category === value || (value === 'all' && !filters.category)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Price Range Filter */}
      <div className="mb-6">
        <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-slate-300">Price Range</h3>
        <div className="flex flex-col gap-2">
          {PRICE_RANGES.map(({ label, min, max }) => (
            <button
              key={label}
              type="button"
              onClick={() => handlePriceRangeChange(min, max)}
              className={cn(
                'w-full rounded-full px-4 py-2 text-sm font-medium transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                isPriceRangeSelected(min, max)
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-white/50 dark:bg-slate-700/30 text-gray-700 dark:text-slate-300 hover:bg-white/80 dark:hover:bg-slate-600/50 border border-gray-200/50 dark:border-slate-600/50'
              )}
              aria-pressed={isPriceRangeSelected(min, max)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Duration Range Filter */}
      <div className="mb-6">
        <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-slate-300">Duration</h3>
        <div className="flex flex-col gap-2">
          {DURATION_RANGES.map(({ label, min, max }) => (
            <button
              key={label}
              type="button"
              onClick={() => handleDurationRangeChange(min, max)}
              className={cn(
                'w-full rounded-full px-4 py-2 text-sm font-medium transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                isDurationRangeSelected(min, max)
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-white/50 dark:bg-slate-700/30 text-gray-700 dark:text-slate-300 hover:bg-white/80 dark:hover:bg-slate-600/50 border border-gray-200/50 dark:border-slate-600/50'
              )}
              aria-pressed={isDurationRangeSelected(min, max)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Clear All Filters */}
      {hasActiveFilters && (
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={onClearFilters}
            leftIcon={<X className="h-4 w-4" />}
          >
            Clear All Filters
          </Button>
        </div>
      )}

      {/* Results Summary */}
      <div className="border-t border-primary-100/50 dark:border-slate-700/50 pt-4 mt-2">
        <p className="text-center text-sm text-gray-600 dark:text-slate-400">
          <span className="font-semibold text-gray-900 dark:text-slate-100">{resultCount}</span>{' '}
          {resultCount === 1 ? 'service' : 'services'} found
        </p>
      </div>
    </div>
  );
};
